import { AgentSearchService } from 'ag-common-svc/lib/services/agent-search';
import { Component, HostBinding, Input, ViewChild } from '@angular/core';
import { Agent, AgentKeys, ChangeSourceType, Lookup, LookupKeys, NEED_TO_KNOW_VISIBILITY_LEVEL_LOOKUP, NeedToKnow, NeedToKnowKeys, NeedToKnowVisibilityLevel } from '@ag-common-lib/public-api';
import ArrayStore from 'devextreme/data/array_store';
import DataSource from 'devextreme/data/data_source';
import { BehaviorSubject, Observable, combineLatest, from } from 'rxjs';
import { filter, map, shareReplay, switchMap } from 'rxjs/operators';
import { ModalWindowComponent } from '../../../modal-window/modal-window.component';
import { QueryParam, WhereFilterOperandKeys } from '../../../../dao/CommonFireStoreDao.dao';
import { AgentNeedToKnowService } from '../../../../services/agent-need-to-know.service';
import { BaseModelKeys } from '@ag-common-lib/lib/models/base.model';
import { AgentMergeQueueGuardService } from '../../../../services/agent-merge-queue-guard.service';
import { MergeInProgressAlertModalService } from '../../../merge-in-progress-alert-modal/merge-in-progress-alert-modal.service';

@Component({
  selector: 'ag-shr-need-to-know',
  templateUrl: './need-to-know.component.html',
  styleUrls: ['./need-to-know.component.scss'],
})
export class NeedToKnowComponent {
  @HostBinding('class') className = 'need-to-know';
  @Input() set agent(agent: Agent) {
    this.agentId$.next(agent[BaseModelKeys.dbId]);
  }
  @Input() canEdit = true;
  @Input() canDelete = true;
  @Input() canCreate = true;
  @Input() sourceType: ChangeSourceType | null = null;
  @Input() set allowedVisibilityLevels(data: NeedToKnowVisibilityLevel[]) {
    if (Array.isArray(data)) {
      this.allowedVisibilityLevels$.next(data);
    }
  }
  @ViewChild('needToKnowModalRef', { static: true }) needToKnowModalComponent: ModalWindowComponent;

  public inProgress$: Observable<boolean>;
  public agentsDataSource$: Observable<any>;
  public BaseModelKeys = BaseModelKeys;
  public NeedToKnowKeys = NeedToKnowKeys;
  public needToKnowVisibilityLevelLookup = NEED_TO_KNOW_VISIBILITY_LEVEL_LOOKUP;
  public needToKnowFormData: NeedToKnow;
  public needToKnow$: Observable<DataSource>;
  public readonly agentId$ = new BehaviorSubject<string>(undefined);

  private allowedVisibilityLevels$ = new BehaviorSubject<NeedToKnowVisibilityLevel[]>([]);

  constructor(
    private agentSearchService: AgentSearchService,
    private agentNeedToKnowService: AgentNeedToKnowService,
    private agentMergeQueueGuardService: AgentMergeQueueGuardService,
    private mergeInProgressAlertModalService: MergeInProgressAlertModalService,
  ) {
    this.needToKnow$ = combineLatest([this.agentId$, this.allowedVisibilityLevels$]).pipe(
      filter(Boolean),
      switchMap(([agentId, allowedVisibilityLevels]) => {
        const qp: QueryParam[] = [];

        if (Array.isArray(allowedVisibilityLevels) && allowedVisibilityLevels?.length) {
          const visibilityLevelsQueryParam = new QueryParam(
            NeedToKnowKeys.visibilityLevel,
            WhereFilterOperandKeys.in,
            allowedVisibilityLevels,
          );
          qp.push(visibilityLevelsQueryParam);
        }

        return this.agentNeedToKnowService.getList(agentId, qp);
      }),
      map(needToKnows => {
        return new DataSource({
          store: new ArrayStore({
            key: 'dbId',
            data: Array.isArray(needToKnows) ? needToKnows : [],
          }),
        });
      }),
      shareReplay(1),
    );

    this.agentsDataSource$ = from(this.agentSearchService.search({ sort: [{ selector: AgentKeys.p_agent_first_name, desc: false }] })).pipe(
      map(response => response?.data ?? []),
      map((response): Partial<Lookup>[] =>
        Array.isArray(response)
          ? response.map((agent: Agent) => {
              const description = [agent?.p_agent_first_name, agent?.p_agent_last_name].filter(Boolean).join(' ');
              const value = agent?.p_email;

              return {
                key: agent?.dbId,
                [LookupKeys.value]: value,
                [LookupKeys.description]: description,
              };
            })
          : [],
      ),
    );
  }

  public showListPopup = async () => {
    const agentDbId = this.agentId$.value;
    const isBlocked = await this.agentMergeQueueGuardService.isAgentMergeBlocked(agentDbId);

    if (isBlocked) {
      this.mergeInProgressAlertModalService.open();
      return;
    }

    this.needToKnowModalComponent.showModal();
  };

  public onRowRemoving = e => {
    e.cancel = this.agentNeedToKnowService.delete(this.agentId$.value, e.data[BaseModelKeys.dbId], this.sourceType);
  };
}
